import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/common/components/ui/dialog';
import { useBlocker, useFetcher, useParams } from 'react-router';
import { Button } from '~/common/components/ui/button';
import { Trans, useTranslation } from 'react-i18next';
import { LucideArrowLeftRight, LucideCheck } from 'lucide-react';
import type { Area} from '~/common/types/area';
import { PgArea, TgArea } from '~/common/types/area';
import { getLanguageValue, webshopConfig } from '~/config/webshopConfig';
import { getLinkRel } from '~/common/utils/urlUtil';
import { useRootPath } from '~/common/hooks/useRootPath';
import type { IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { AREA_SELECTION_PATH, SHORTCUT_RESOLVER_PATH } from '~/route-constants.ts';
import { fallbackLng } from '~/i18nConfig';

interface AreaChangeDialogProps {
  loginInfo?: IWebshopLoginInfoDtoData;
}

/**
 * Observes the location change and shows the dialog if the area changes.
 */
export function AreaChangeConfirmationDialog({ loginInfo }: Readonly<AreaChangeDialogProps>) {
  const { t, i18n } = useTranslation();
  const fetcher = useFetcher();
  const { area, lang: langParam } = useParams();
  const { rootPath } = useRootPath();
  const lang = langParam ?? fallbackLng;

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const currentParts = currentLocation.pathname?.split('/');
    const currentArea = currentParts[2];
    const nextParts = nextLocation.pathname?.split('/');
    const nextArea = nextParts[2];

    return (
      currentArea !== nextArea &&
      nextArea?.toLowerCase() !== AREA_SELECTION_PATH && // Ignore area selection, because it's a special case where the user is asked to change the area
      nextLocation.pathname?.toLowerCase() !== `/${SHORTCUT_RESOLVER_PATH}` && // die ShortcutResoverRoute zeigt selbst einen Area-Selection Dialog an, wenn die Area nicht herausgefunden wird
      nextLocation.pathname !== rootPath
    );
  });

  const handleCancel = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleConfirm = (targetArea: Area) => {
    if (loginInfo?.customerType === 'MULTICHANNEL') {
      // Redirect without logout
      const formData = new FormData();
      formData.append('redirectUrl', `/${lang}/${targetArea}`);
      fetcher.submit(formData, { method: 'post', action: '/infra/auth/login' });
    } else {
      const formData = new FormData();
      formData.append('redirectTo', `/${lang}/${targetArea}`);
      fetcher.submit(formData, { method: 'post', action: '/infra/auth/logout' });
    }
  };

  if (!loginInfo || blocker.state !== 'blocked') {
    return null;
  }

  const isPgArea = area === PgArea;
  const agbLink = getLanguageValue(i18n.language, webshopConfig.agbLink);

  return (
    <Dialog open={blocker.state === 'blocked'} onOpenChange={handleCancel}>
      <DialogContent showFullscreenOnMobile data-testid={'areachange-dialog'}>
        <DialogHeader>
          <DialogTitle className={'flex items-center'}>
            <LucideArrowLeftRight size={36} className={'mr-4'} />
            {t(
              isPgArea
                ? 'Möchten Sie zum Transgourmet-Webshop (Belieferung) wechseln?'
                : 'Möchten Sie zu Prodega-Easy (Abholung) wechseln?'
            )}
          </DialogTitle>
        </DialogHeader>
        <div>
          <Trans
            t={t}
            i18nKey={
              isPgArea
                ? 'Der Transgourmet-Webshop ist für unsere Belieferungskunden. Bitte beachten Sie unsere <agbLink>AGB</agbLink>.'
                : 'Prodega-Easy ist für unsere Abholkunden. Bitte beachten Sie unsere <agbLink>AGB</agbLink>.'
            }
            components={{
              agbLink: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  className={'font-bold text-black hover:text-primary underline underline-offset-2'}
                  target={'_blank'}
                  href={agbLink}
                  rel={getLinkRel(agbLink)}
                />
              )
            }}
          />
          {loginInfo?.customerType !== 'MULTICHANNEL' && (
            <div className={'mt-4'}>
              <strong>{t('Sie werden automatisch abgemeldet.')}</strong>
            </div>
          )}
        </div>
        <DialogFooter className={'flex sm:justify-end gap-2'}>
          <DialogClose asChild>
            <Button variant={'primaryOutline'} onClick={handleCancel} data-testid={'cancel-button'}>
              {t('Abbrechen')}
            </Button>
          </DialogClose>
          <Button onClick={() => handleConfirm(isPgArea ? TgArea : PgArea)} data-testid={'confirm-button'}>
            <LucideCheck size={18} className={'mr-2'} />
            {t('Jetzt wechseln')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
